import { useEffect, useState } from 'react';

import { createPortal } from 'react-dom';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { BookOpen } from 'lucide-react';

import { ParticipantsList } from '@cctv/components';
import { DRAW_SIZE, cosmeticAssetUrl, sortCosmetics } from '@cctv/components/Cosmetics';
import { useExperience } from '@cctv/contexts/ExperienceContext';
import { useExperienceState } from '@cctv/contexts/ExperienceStateContext';
import { useUser } from '@cctv/contexts/UserContext';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@cctv/core';
import ExperienceBlockContainer from '@cctv/experiences/ExperienceBlockContainer/ExperienceBlockContainer';
import { useClearAvatars } from '@cctv/hooks/useClearAvatars';
import { AvatarData, Block, BlockKind, SubmissionState } from '@cctv/types';
import { keyedStrokes } from '@cctv/utils';

import AdminNotification from './AdminNotification/AdminNotification';

import styles from './Experience.module.scss';

function deriveCurrentBlock(blocks: Block[], submissionState: SubmissionState): Block | undefined {
  if (blocks.length <= 1) return blocks[0];

  const [parent, ...children] = blocks;
  if (parent.kind === BlockKind.FAMILY_FEUD) {
    const unanswered = children.find((child) => !submissionState[child.id]);
    return unanswered ?? parent;
  }

  return blocks[0];
}

function AvatarCircle({ avatar }: { avatar?: AvatarData | null }) {
  if (avatar?.image) {
    return (
      <div className={styles.avatarCircleSvg} style={{ position: 'relative', overflow: 'hidden' }}>
        <img
          src={avatar.image}
          alt=""
          style={{ width: '100%', height: '100%', display: 'block' }}
        />
        {sortCosmetics(Array.isArray(avatar.cosmetics) ? avatar.cosmetics : []).map((c) => {
          const url = cosmeticAssetUrl(c.asset_key);
          if (!url) return null;
          return (
            <img
              key={`${c.cosmetic_id}-${c.x}-${c.y}-${c.rotation}`}
              src={url}
              alt=""
              style={{
                position: 'absolute',
                left: `${(c.x / DRAW_SIZE) * 100}%`,
                top: `${(c.y / DRAW_SIZE) * 100}%`,
                width: `${(c.width / DRAW_SIZE) * 100}%`,
                height: `${(c.height / DRAW_SIZE) * 100}%`,
                transform: c.rotation ? `rotate(${c.rotation}deg)` : undefined,
                transformOrigin: 'center',
                pointerEvents: 'none',
              }}
            />
          );
        })}
      </div>
    );
  }

  const strokes = (avatar?.strokes ?? []).filter((s) => Array.isArray(s.points));
  if (!strokes.length) {
    return (
      <svg viewBox="0 0 100 100" className={styles.avatarCircleSvg} aria-hidden>
        <circle cx="50" cy="36" r="18" fill="currentColor" opacity="0.45" />
        <ellipse cx="50" cy="82" rx="28" ry="22" fill="currentColor" opacity="0.45" />
      </svg>
    );
  }

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const stroke of strokes) {
    for (let j = 0; j < stroke.points.length; j += 2) {
      minX = Math.min(minX, stroke.points[j]);
      maxX = Math.max(maxX, stroke.points[j]);
      minY = Math.min(minY, stroke.points[j + 1]);
      maxY = Math.max(maxY, stroke.points[j + 1]);
    }
  }

  const pad = 12;
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const half = Math.max(maxX - minX, maxY - minY) / 2 + pad;
  const viewBox = `${cx - half} ${cy - half} ${half * 2} ${half * 2}`;

  return (
    <svg viewBox={viewBox} className={styles.avatarCircleSvg} aria-hidden>
      {keyedStrokes(strokes).map(({ key, stroke }) => {
        const pts: string[] = [];
        for (let j = 0; j < stroke.points.length; j += 2) {
          pts.push(`${stroke.points[j]},${stroke.points[j + 1]}`);
        }
        return (
          <polyline
            key={key}
            points={pts.join(' ')}
            stroke={stroke.color}
            strokeWidth={Math.max(Math.min(stroke.width * 0.25, 4), 1)}
            vectorEffect="non-scaling-stroke"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        );
      })}
    </svg>
  );
}

export default function Experience() {
  const navigate = useNavigate();
  const { state: locationState } = useLocation();
  const { experience, participant, code, isLoading, experienceStatus, error } = useExperience();
  const { submissionState } = useExperienceState();
  const { isAdmin } = useUser();
  const { clearAvatars, isLoading: clearing } = useClearAvatars();
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  const participants = experience?.participants || [];
  const needsAvatar = !isAdmin && !participant?.avatar;
  const hasInitialData = !isAdmin ? experience && participant : experience;

  useEffect(() => {
    if (!isAdmin && participant) {
      document.body.classList.add('has-avatar-btn');
      return () => document.body.classList.remove('has-avatar-btn');
    }
  }, [isAdmin, participant]);

  const avatarBtn =
    !isAdmin && participant
      ? createPortal(
          <button
            className={styles.avatarToggleBtn}
            aria-label="Edit avatar"
            title="Edit avatar"
            onClick={() => navigate(`/experiences/${code}/avatar`)}
          >
            <AvatarCircle avatar={participant.avatar} />
          </button>,
          document.body,
        )
      : null;

  useEffect(() => {
    if (locationState?.avatarSubmitted) return;
    if (needsAvatar && code && !isLoading && hasInitialData) {
      navigate(`/experiences/${code}/avatar`, { replace: true });
    }
  }, [locationState, needsAvatar, code, navigate, isLoading, hasInitialData]);

  if (isLoading) {
    return (
      <section className="page">
        <h1 className={styles.title}>{code || 'Experience'}</h1>
        <p className={styles.subtitle}>Preparing experience…</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="page">
        <h1 className={styles.title}>{code || 'Experience'}</h1>
        <p className={styles.error}>{error || 'Something went wrong'}</p>
        <div className={styles.errorActions}>
          <Link to="/join" className={styles.link}>
            Join Different Experience
          </Link>
        </div>
      </section>
    );
  }
  // Avatar comes first: while a participant still needs one, don't render any
  // block (the redirect effect above sends them to the avatar editor). This
  // prevents flashing/interacting with a live block before drawing.
  if (needsAvatar && !locationState?.avatarSubmitted) {
    return (
      <section className="page">
        <h1 className={styles.title}>{experience?.name || code}</h1>
        <p className={styles.subtitle}>Preparing experience…</p>
      </section>
    );
  }

  const currentBlock = deriveCurrentBlock(experience?.blocks ?? [], submissionState);

  if (experienceStatus === 'lobby') {
    if (currentBlock && participant) {
      return (
        <section className="page">
          <div className={styles.activeExperience}>
            <h1 className={styles.title}>{experience?.name || code}</h1>

            {isAdmin && !participant && <AdminNotification code={code!} />}

            <div className={styles.experienceContent}>
              <div className={styles.activeBlock}>
                <ExperienceBlockContainer block={currentBlock} participant={participant} />
              </div>
            </div>

            {experience?.playbill_enabled !== false && (
              <Link
                to={`/experiences/${code}/playbill`}
                className={styles.fab}
                aria-label="Open Playbill"
              >
                <BookOpen size={22} />
              </Link>
            )}
          </div>
          {avatarBtn}
        </section>
      );
    }

    return (
      <section className="page">
        <div className={styles.lobbyGrid}>
          <div className={styles.topArea}>
            {experience && (
              <div className={styles.experienceInfo}>
                <h2 className={styles.experienceName}>{experience.name}</h2>
              </div>
            )}

            {isAdmin && !participant && <AdminNotification code={code!} />}
          </div>

          <div className={styles.middleArea}>
            <div className={styles.participantsContainer}>
              <h4 className={styles.participantsTitle}>Players in Lobby:</h4>
              {experience && participants.length > 0 ? (
                <ParticipantsList
                  participants={participants}
                  highlightUserId={participant?.user_id}
                  showRole={false}
                />
              ) : (
                <p className={styles.loadingParticipants}>
                  {experience ? 'No participants yet...' : 'Loading participants...'}
                </p>
              )}
            </div>

            <p className={styles.waitingMessage}>Waiting for the experience to start...</p>
          </div>

          {/* Admin/Host control: clear avatars (lobby view without a current block) */}
          {(isAdmin || experience?.hosts?.some((h) => h.user_id === participant?.user_id)) && (
            <div className={styles.adminControls}>
              <Button onClick={() => setConfirmClearOpen(true)} disabled={clearing}>
                {clearing ? 'Clearing…' : 'Clear Avatars'}
              </Button>
              <Dialog open={confirmClearOpen} onOpenChange={setConfirmClearOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Clear all avatars?</DialogTitle>
                    <DialogDescription>
                      This will erase every participant&apos;s avatar and drawings. It cannot be
                      undone.
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button variant="secondary" onClick={() => setConfirmClearOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={async () => {
                        setConfirmClearOpen(false);
                        await clearAvatars();
                      }}
                      disabled={clearing}
                    >
                      Clear Avatars
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          )}

          <div className={styles.bottomActions}>
            {isAdmin && (
              <Button to={`/experiences/${code}/manage`} variant="secondary">
                Manage
              </Button>
            )}
            {!needsAvatar && experience?.playbill_enabled !== false && (
              <Button to={`/experiences/${code}/playbill`}>Open Playbill</Button>
            )}
          </div>
        </div>
        {avatarBtn}
      </section>
    );
  }

  // Active experience state (for when the experience is live or paused)
  if (experienceStatus === 'live' || experienceStatus === 'paused') {
    return (
      <section className="page">
        <div className={styles.activeExperience}>
          <h1 className={styles.title}>{experience?.name || code}</h1>

          {isAdmin && !participant && <AdminNotification code={code!} />}

          <div className={styles.experienceContent}>
            {currentBlock && participant ? (
              <div className={styles.activeBlock}>
                <ExperienceBlockContainer
                  block={currentBlock}
                  participant={participant}
                  disabled={experience?.status === 'paused'}
                />
              </div>
            ) : (
              <div className={styles.waitingForBlock}>
                <p>
                  {experience?.status === 'paused'
                    ? 'Experience is paused...'
                    : 'Waiting for the next activity...'}
                </p>
              </div>
            )}
          </div>

          <Link
            to={`/experiences/${code}/playbill`}
            className={styles.fab}
            aria-label="Open Playbill"
          >
            <BookOpen size={22} />
          </Link>
        </div>
        {avatarBtn}
      </section>
    );
  }

  // Fallback
  return (
    <section className="page">
      <h1 className={styles.title}>{code}</h1>
      <p className={styles.subtitle}>Initializing experience...</p>
    </section>
  );
}
